import { DialogOptions } from 'components/core/Dialog/common/Dialog';
import { AsyncDialogOptions } from 'components/core/Dialog/common/useAsyncDialog';
import React from 'react';
import { FormattedMessage, formatTranslation } from 'intl';
import { DialogTranslationKeys } from 'intl/keys';
import { IntlShape, useIntl } from 'react-intl';
import { SelectedItemBreakdown } from 'shared/domain/documentation/operationPreflight';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';

function renderBreakdownList(
  breakdown: SelectedItemBreakdown[],
  intl: IntlShape
): React.ReactNode[] {
  return breakdown.map((item, index) => {
    const isLast = index === breakdown.length - 1;
    const separator = isLast ? '' : ', ';

    if (item.type === 'directory') {
      const countSuffix = formatTranslation(
        intl,
        DocumentationTranslationKeys.itemFolderCountSuffix,
        { count: item.nestedCount }
      );

      return (
        <React.Fragment key={item.localId}>
          <strong>{item.name}</strong> {countSuffix}{separator}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={item.localId}>
        <strong>{item.name}</strong>{separator}
      </React.Fragment>
    );
  });
}

function BreakdownDescription({
  breakdown,
}: {
  breakdown: SelectedItemBreakdown[];
}): React.ReactElement {
  const intl = useIntl();
  const itemsList = renderBreakdownList(breakdown, intl);

  return (
    <FormattedMessage id={DialogTranslationKeys.confirmationRestoreDocumentationBreakdown}
      values={{
        itemsList: <>{itemsList}</>,
      }}
    />
  );
}

function getRestoreDescription(
  filesCount: number,
  foldersCount: number,
  breakdown?: SelectedItemBreakdown[]
): React.ReactNode {
  const hasBreakdown = breakdown && breakdown.length > 0;

  if (hasBreakdown) {
    return <BreakdownDescription breakdown={breakdown} />;
  }

  const hasFiles = filesCount > 0;
  const hasFolders = foldersCount > 0;

  if (hasFiles && hasFolders) {
    return (
      <FormattedMessage id={DialogTranslationKeys.confirmationRestoreDocumentationFilesFolders}
        values={{ filesCount, foldersCount }}
      />
    );
  } else if (hasFolders) {
    return (
      <FormattedMessage id={DialogTranslationKeys.confirmationRestoreDocumentationFolders}
        values={{ selectedCount: foldersCount }}
      />
    );
  } else {
    return (
      <FormattedMessage id={DialogTranslationKeys.confirmationRestoreDocumentation}
        values={{ selectedCount: filesCount }}
      />
    );
  }
}

export function documentationRestoreDialog(
  filesCount: number,
  foldersCount: number = 0,
  breakdown?: SelectedItemBreakdown[]
): DialogOptions {
  return {
    catchOnCancel: false,
    title: (
      <FormattedMessage id={DialogTranslationKeys.confirmationRestoreDocumentationTitle} />
    ),
    description: (
      <span>{getRestoreDescription(filesCount, foldersCount, breakdown)}</span>
    ),
  };
}

export function documentationRestoreAsyncDialog(
  filesCount: number,
  foldersCount: number = 0,
  breakdown?: SelectedItemBreakdown[]
): AsyncDialogOptions {
  return {
    title: (
      <FormattedMessage id={DialogTranslationKeys.confirmationRestoreDocumentationTitle} />
    ),
    description: (
      <span>{getRestoreDescription(filesCount, foldersCount, breakdown)}</span>
    ),
    submitLabel: CommonTranslationKeys.restore,
  };
}
